<template>
  <div v-if="showBanner" class="cookie-overlay">
    <div class="cookie-banner" role="dialog" aria-label="Cookie Consent" aria-modal="true">
      <h2 class="cookie-title">{{ $t('cookieConsent.title') }}</h2>
      <p class="cookie-message">
        {{ $t('cookieConsent.message') }}
        <router-link :to="{ name: 'home', hash: '#imprint' }" class="cookie-link">{{ $t('cookieConsent.privacyPolicyLink') }}</router-link>.
      </p>

      <div v-if="showSettings" class="cookie-settings">
        <div class="cookie-category">
          <div class="cookie-category-header">
            <label class="cookie-toggle">
              <input type="checkbox" checked disabled />
              <span class="toggle-slider disabled"></span>
            </label>
            <strong>{{ $t('cookieConsent.essential.title') }}</strong>
          </div>
          <p class="cookie-category-desc">{{ $t('cookieConsent.essential.description') }}</p>
        </div>

        <div class="cookie-category">
          <div class="cookie-category-header">
            <label class="cookie-toggle">
              <input type="checkbox" v-model="analyticsConsent" />
              <span class="toggle-slider"></span>
            </label>
            <strong>{{ $t('cookieConsent.analytics.title') }}</strong>
          </div>
          <p class="cookie-category-desc">{{ $t('cookieConsent.analytics.description') }}</p>
        </div>

        <div class="cookie-category">
          <div class="cookie-category-header">
            <label class="cookie-toggle">
              <input type="checkbox" v-model="marketingConsent" />
              <span class="toggle-slider"></span>
            </label>
            <strong>{{ $t('cookieConsent.marketing.title') }}</strong>
          </div>
          <p class="cookie-category-desc">{{ $t('cookieConsent.marketing.description') }}</p>
        </div>

        <p class="cookie-legal">{{ $t('cookieConsent.legalBasis') }}</p>
        <p class="cookie-legal">{{ $t('cookieConsent.dataController') }}</p>
      </div>

      <div class="cookie-actions">
        <button class="cookie-btn cookie-btn-accept" @click="acceptAll">{{ $t('cookieConsent.acceptAll') }}</button>
        <button class="cookie-btn cookie-btn-reject" @click="rejectAll">{{ $t('cookieConsent.rejectAll') }}</button>
        <button v-if="!showSettings" class="cookie-btn cookie-btn-settings" @click="showSettings = true">{{ $t('cookieConsent.settings') }}</button>
        <button v-if="showSettings" class="cookie-btn cookie-btn-save" @click="savePreferences">{{ $t('cookieConsent.save') }}</button>
      </div>
    </div>
  </div>

  <button v-if="!showBanner && consentGiven" class="cookie-reopen-btn" @click="reopenBanner" :aria-label="$t('cookieConsent.settings')">
    🍪
  </button>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const CONSENT_KEY = 'cookie_consent'

const showBanner = ref(false)
const showSettings = ref(false)
const consentGiven = ref(false)
const analyticsConsent = ref(false)
const marketingConsent = ref(false)

function getConsent() {
  try {
    const stored = localStorage.getItem(CONSENT_KEY)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

function setConsent(consent) {
  localStorage.setItem(CONSENT_KEY, JSON.stringify({
    ...consent,
    timestamp: new Date().toISOString()
  }))
  consentGiven.value = true
  showBanner.value = false
  showSettings.value = false
}

function acceptAll() {
  setConsent({ essential: true, analytics: true, marketing: true })
}

function rejectAll() {
  setConsent({ essential: true, analytics: false, marketing: false })
}

function savePreferences() {
  setConsent({
    essential: true,
    analytics: analyticsConsent.value,
    marketing: marketingConsent.value
  })
}

function reopenBanner() {
  const existing = getConsent()
  if (existing) {
    analyticsConsent.value = existing.analytics || false
    marketingConsent.value = existing.marketing || false
  }
  showSettings.value = true
  showBanner.value = true
}

onMounted(() => {
  const existing = getConsent()
  if (existing) {
    consentGiven.value = true
    analyticsConsent.value = existing.analytics || false
    marketingConsent.value = existing.marketing || false
  } else {
    showBanner.value = true
  }
})
</script>

<style scoped>
.cookie-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 1rem;
}

.cookie-banner {
  background: #fff;
  border-radius: 12px 12px 0 0;
  padding: 1.5rem 2rem;
  max-width: 720px;
  width: 100%;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  overflow-y: auto;
}

.cookie-title {
  font-size: 1.25rem;
  margin: 0 0 0.75rem;
  color: #1a1a1a;
}

.cookie-message {
  font-size: 0.9rem;
  line-height: 1.5;
  color: #444;
  margin: 0 0 1rem;
}

.cookie-link {
  color: #0055ff;
  text-decoration: underline;
}

.cookie-settings {
  border-top: 1px solid #eee;
  padding-top: 1rem;
  margin-bottom: 1rem;
}

.cookie-category {
  margin-bottom: 1rem;
}

.cookie-category-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
}

.cookie-category-desc {
  font-size: 0.8rem;
  color: #666;
  margin: 0 0 0 3.25rem;
  line-height: 1.4;
}

.cookie-toggle {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
  flex-shrink: 0;
}

.cookie-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: #ccc;
  border-radius: 22px;
  transition: 0.3s;
}

.toggle-slider::before {
  content: "";
  position: absolute;
  height: 16px;
  width: 16px;
  left: 3px;
  bottom: 3px;
  background-color: #fff;
  border-radius: 50%;
  transition: 0.3s;
}

.cookie-toggle input:checked + .toggle-slider {
  background-color: #0055ff;
}

.cookie-toggle input:checked + .toggle-slider::before {
  transform: translateX(18px);
}

.toggle-slider.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cookie-legal {
  font-size: 0.75rem;
  color: #888;
  margin: 0.5rem 0;
  line-height: 1.4;
}

.cookie-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.cookie-btn {
  padding: 0.6rem 1.25rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  font-weight: 600;
  transition: background 0.2s;
}

.cookie-btn-accept {
  background: #0055ff;
  color: #fff;
}

.cookie-btn-accept:hover {
  background: #0044cc;
}

.cookie-btn-reject {
  background: #f0f0f0;
  color: #333;
}

.cookie-btn-reject:hover {
  background: #e0e0e0;
}

.cookie-btn-settings,
.cookie-btn-save {
  background: transparent;
  color: #0055ff;
  border: 1px solid #0055ff;
}

.cookie-btn-settings:hover,
.cookie-btn-save:hover {
  background: #e2f1ff;
}

.cookie-reopen-btn {
  position: fixed;
  bottom: 1rem;
  left: 1rem;
  z-index: 9999;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  font-size: 1.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
}

.cookie-reopen-btn:hover {
  transform: scale(1.1);
}

@media (max-width: 600px) {
  .cookie-banner {
    padding: 1rem;
  }

  .cookie-actions {
    flex-direction: column;
  }

  .cookie-btn {
    width: 100%;
    text-align: center;
  }
}
</style>
